import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface QuickBookContextValue {
    isOpen: boolean;
    open: () => void;
    close: () => void;
}

const QuickBookContext = createContext<QuickBookContextValue | null>(null);

/**
 * Provides a global open/close handle for the Quick Book (new job) slide-in
 * panel. Mounted high in the app shell so any screen — top bar, page, etc. —
 * can trigger the panel via `useQuickBook().open()` without navigating.
 */
export function QuickBookProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);
    return <QuickBookContext.Provider value={value}>{children}</QuickBookContext.Provider>;
}

export function useQuickBook(): QuickBookContextValue {
    const ctx = useContext(QuickBookContext);
    if (!ctx) {
        throw new Error("useQuickBook must be used within a QuickBookProvider");
    }
    return ctx;
}
